'use client';

import { Download, QrCode, RefreshCw } from 'lucide-react';
import Image from 'next/image';
import { QRCodeCanvas } from 'qrcode.react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { useStaticQr } from '@/features/totems/presentation/hooks/use-static-qr';
import { cn } from '@/shared/utils/utils';

interface Props {
  totemId?: string;
  staticQrEnabled?: boolean;
}

const QR_SIZE = 256;

export function MpStaticQrBadge({ totemId, staticQrEnabled }: Props) {
  const [open, setOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useStaticQr(totemId ?? '');

  if (!totemId) return null;

  const isConfigured = Boolean(staticQrEnabled && data && data.ok);

  function handleDownloadImage(dataUrl: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `qr-estatico-mercadopago-${totemId}.png`;
    link.click();
  }

  function handleDownloadCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `qr-estatico-mercadopago-${totemId}.png`;
    link.click();
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold transition-all border cursor-pointer',
          isConfigured
            ? 'border-[#009ee3]/30 bg-[#009ee3]/10 text-[#009ee3] hover:bg-[#009ee3]/20'
            : 'border-border/60 bg-muted/30 text-muted-foreground hover:bg-muted/50'
        )}
        title="Código QR de Mercado Pago para mostrador"
      >
        <Image
          src="/mercado-pago.svg"
          alt="MP"
          width={14}
          height={14}
          className="object-contain shrink-0"
        />
        <span className="hidden md:inline">
          {isConfigured ? 'QR Mercado Pago' : 'QR no activo'}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="size-5 text-[#009ee3]" />
              QR de Mercado Pago (Caja)
            </DialogTitle>
            <DialogDescription>
              Código QR estático oficial asignado a este punto de venta para pagos en mostrador.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center gap-4 py-4">
            {isLoading ? (
              <Skeleton className="size-64 rounded-xl" />
            ) : isError ? (
              <div className="text-center p-6 space-y-2">
                <p className="text-sm text-destructive">
                  {error instanceof Error ? error.message : 'No se pudo obtener el QR de Mercado Pago.'}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
                  {isFetching ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
                  Reintentar
                </Button>
              </div>
            ) : data && data.ok ? (
              <>
                <div className="flex items-center justify-center rounded-2xl border bg-white p-4 shadow-sm">
                  {data.kind === 'image_url' ? (
                    <img
                      src={data.value}
                      alt="QR Mercado Pago"
                      width={QR_SIZE}
                      height={QR_SIZE}
                      className="size-64 object-contain"
                    />
                  ) : (
                    <QRCodeCanvas
                      ref={canvasRef}
                      value={data.value}
                      size={QR_SIZE}
                      level="H"
                      includeMargin
                    />
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  Los clientes pueden escanear este QR con la aplicación de Mercado Pago para abonar su pedido en el mostrador.
                </p>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 text-xs"
                    onClick={() => {
                      if (data.kind === 'image_url') {
                        handleDownloadImage(data.value);
                      } else {
                        handleDownloadCanvas();
                      }
                    }}
                  >
                    <Download className="size-3.5" />
                    Descargar QR
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center p-6 space-y-2 text-muted-foreground text-sm">
                <p>El código QR no se encuentra habilitado para este punto de venta.</p>
                <p className="text-xs">Podés activarlo desde la configuración de Puntos de Venta (tótems/caja).</p>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t">
            <Button type="button" size="sm" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
